/* eslint-disable no-underscore-dangle */
import lod_ from "lodash";
import csvDownload from "json-to-csv-export";
import { read, utils } from "xlsx";
import {
	Badge,
	Card,
	CardContent,
	Divider,
	Icon,
	IconButton,
	Menu,
	MenuItem,
	Tooltip
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import DefaultDataTable from "components/Custom/Tables/DefaultDataTable";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import ConfirmDialogButton from "components/Custom/Dialogs/ConfirmDialogButton";
import MDButton from "components/Basics/MDButton";
import { display } from "redux-react/reducers/snackBarReducer";
import csvTemplate from "assets/template/template_theme_faibrik.csv";
import { v4 } from "uuid";
import { useTranslation } from "react-i18next";
import LanguagesHelp from "./LanguagesHelp";
import LanguagesCell from "./LanguagesCell";
import { useFormActions } from "hooks/useFormActions";

/**
 * Theme Card component
 * Displays a card with theme information and allows editing and deleting themes.
 * @param {Object} props - Component properties
 * @param {Array} props.folders - List of folders
 * @param {Object} props.theme - Theme object to display
 * @param {Function} props.onEdit - Function to call when editing a theme
 * @param {Function} props.handleDeleteFolder - Function to call when deleting a theme
 * @param {Function} props.getFolders - Function to fetch folders (optional)
 * @returns {JSX.Element} Rendered ThemeCard component
 */
const ThemeCard = ({
	folders,
	theme,
	onEdit,
	handleDeleteFolder,
	getFolders = () => {},
	onAddFile = () => {},
	themeActions
}) => {
	const dispatch = useDispatch();
	const { startCategoryTraining, startCategoryUntraining, translateElements } = themeActions;
	const { getItemsFromCollection, deleteItems } = useFormActions();

	const [selectedRows, setSelectedRows] = useState([]);
	const [categories, setCategories] = useState([]);

	const [menuRef, setMenuRef] = useState(null);
	const [menuValues, setMenuValues] = useState({});
	const [fileInputRef, setFileInputRef] = useState(null);
	const [anchorElMenu, setAnchorElMenu] = useState(null);

	const { t, ready } = useTranslation(); // Hook to access translations
	const menuRefTrigger = useRef();

	/**
	 * Convert input categories to a format suitable for CSV export
	 * @param {Array} inputCategories - Array of categories with questions and answers
	 * @returns {Array} Transformed data ready for CSV export
	 */
	function transformData(inputCategories) {
		const result = [];

		inputCategories.forEach(category => {
			const languages = Object.keys(category.label);

			languages.forEach(lang => {
				const categoryLabel = category.label[lang]?.trim() || "";

				category.questions.forEach(question => {
					const questionLabel = question.question.label[lang]?.trim() || "";
					const answerLabel = question.answer.label[lang]?.trim() || "";

					result.push({
						language: lang,
						category_id: category.code,
						question_id: question.code,
						category: categoryLabel,
						question: questionLabel,
						answer: answerLabel
					});
				});
			});
		});

		return result;
	}

	/**
	 * Fetch the theme tree and download it as a CSV file
	 */
	const getTreeTheme = () => {
		dispatch(
			themeActions.getTreeTheme(theme?.code, theme => {
				if (!lod_.isNil(theme)) {
					const csvData = transformData(theme.categories);
					csvDownload({
						data: csvData,
						filename: `${theme.name}.csv`,
						delimiter: ";"
					});
				}
			})
		);
	};

	/**
	 * Fetch categories for the theme
	 */
	const getCategories = async () => {
		const fetchedCategories = await getItemsFromCollection("answerItem", {
			query: {
				documentType: "answerType",
				type: "themeCategory",
				parent: theme?.code
			},
			sort: {
				name: 1
			}
		});

		setCategories(fetchedCategories);
	};

	/**
	 * Handle file import - supports CSV, XLSX and other table formats
	 * @param {Event} event - File input change event
	 */
	const handleFileImport = event => {
		const file = event.target.files[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = e => {
			try {
				const workbook = read(e.target.result, { type: "binary", codepage: 65001 });
				// Get the first worksheet
				const worksheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[worksheetName];

				// Import all fields as strings
				const jsonWithHeaders = utils.sheet_to_json(worksheet, {
					raw: false, // Don't use raw values, convert everything to strings
					defval: "", // Default value for empty cells
					blankrows: false // Skip blank rows
				});

				dispatch(
					themeActions.insertTreeFromJSON(
						{
							code: theme.code,
							documents: jsonWithHeaders
						},
						() => {
							getFolders();
						},
						() => {
							getFolders();
							dispatch(
								display({
									message: t("ASSETS.THEME.cannotImportCSV"),
									type: "error"
								})
							);
						}
					)
				);
			} catch {
				display({
					message: t("ASSETS.THEME.cannotImportCSV"),
					type: "error"
				});
			}
		};

		reader.readAsArrayBuffer(file);
		// Reset the input value to allow importing the same file again
		event.target.value = "";
	};

	/**
	 * Trigger file input click
	 */
	const triggerFileImport = () => {
		if (fileInputRef) {
			fileInputRef.click();
		}
	};

	/**
	 * Download the CSV template file for users to understand the expected format
	 */
	const downloadTemplate = () => {
		// Create a link to download the template file from assets
		const link = document.createElement("a");
		link.href = csvTemplate;
		link.download = "template_theme_faibrik.csv";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	/**
	 * Get answer items by type and parent codes
	 * @param {string} type
	 * @param {array} parentCodes
	 * @returns {Promise<Array>}
	 */
	const getAnswerItems = async (type, parentCodes) => {
		return getItemsFromCollection("answerItem", {
			query: {
				documentType: "answerType",
				type,
				parent: {
					$in: parentCodes
				}
			},
			projection: {
				_id: 0,
				code: 1
			}
		});
	};

	/**
	 * Translate all categories names from a theme
	 */
	const translateCategoryName = async codes => {
		const TYPE = "themeCategory";
		translateElements(codes, TYPE, () => {
			getCategories();
		});
	};

	/**
	 * Translate all questions from a theme
	 */
	const translateQuestions = async codes => {
		const TYPE = "themeQuestion";
		const questions = await getAnswerItems(TYPE, codes);
		const questionsCodes = questions.map(row => row.code);
		translateElements(questionsCodes, "themeCategory");
	};

	const HEADER_ACTIONS = [
		<MDBox>
			<Badge
				badgeContent={selectedRows.length}
				color="error"
				anchorOrigin={{
					vertical: "top",
					horizontal: "right"
				}}
			>
				<MDButton
					sx={{ ml: 0.5 }}
					// size="small"
					// disabled={lod_.isEmpty(selectedRows)}
					variant="contained"
					color="dark"
					onClick={e => {
						setAnchorElMenu(e.currentTarget);
					}}
				>
					<Icon>list_alt</Icon>&nbsp;Actions
				</MDButton>
			</Badge>
			{/* Menu */}
			<Menu
				anchorEl={anchorElMenu}
				open={Boolean(anchorElMenu)}
				onClose={() => setAnchorElMenu(null)}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center"
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center"
				}}
			>
				{/* Actions list */}
				<MenuItem disabled style={{ opacity: 1 }}>
					<MDTypography variant="caption">{t("ASSETS.THEME.actionList")}</MDTypography>
				</MenuItem>

				{/* New category */}
				<MenuItem
					onClick={() => {
						onAddFile(theme);
						setAnchorElMenu(null);
					}}
				>
					<Icon fontSize="medium">add</Icon> {t("ASSETS.THEME.createCategory")}
				</MenuItem>

				{/* Export CSV theme */}
				<Tooltip title={t("ASSETS.THEME.CATEGORY.downloadCSVTooltip")} placement="left">
					<MenuItem onClick={getTreeTheme}>
						<Icon fontSize="medium">download</Icon> {t("SETTINGS.export")}
					</MenuItem>
				</Tooltip>

				{/* Import CSV file */}
				{/* Hidden file input */}
				<input
					ref={ref => setFileInputRef(ref)}
					type="file"
					accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
					onChange={handleFileImport}
					style={{ display: "none" }}
				/>
				{/* Button */}
				<ConfirmDialogButton
					onCancel={() => {
						setAnchorElMenu(null);
					}}
					onConfirm={(e, values) => {
						triggerFileImport();
						setAnchorElMenu(null);
					}}
					component={
						<Tooltip title={t("ASSETS.THEME.CATEGORY.uploadFileTooltip")} placement="left">
							<MenuItem>
								<Icon fontSize="medium">upload_file</Icon> {t("SETTINGS.import")}
							</MenuItem>
						</Tooltip>
					}
					title={t("ASSETS.THEME.importFileTitle")}
					content={t("ASSETS.THEME.importFileContent")}
				/>

				<Tooltip title={t("ASSETS.THEME.CATEGORY.downloadTemplateTooltip")} placement="left">
					<MenuItem onClick={downloadTemplate}>
						<Icon fontSize="medium">description</Icon> {t("ASSETS.THEME.downloadTemplate")}
					</MenuItem>
				</Tooltip>

				<Divider />

				{/* Actions selection */}
				<MenuItem disabled style={{ opacity: 1 }}>
					<MDTypography variant="caption">{t("ASSETS.THEME.actionSelection")}</MDTypography>
				</MenuItem>

				{/* Translate name */}
				<MenuItem
					onClick={() => {
						const codes = selectedRows.map(row => row.code);
						translateCategoryName(codes);
						setSelectedRows([]);
						setAnchorElMenu(null);
					}}
					disabled={selectedRows.length < 1}
				>
					<Icon fontSize="medium">translate</Icon>
					{t("ASSETS.THEME.ACTIONS.translateCategoriesName")}
				</MenuItem>
				{/* Translate content */}
				<MenuItem
					onClick={() => {
						const codes = selectedRows.map(row => row.code);
						translateQuestions(codes);
						setSelectedRows([]);
						setAnchorElMenu(null);
					}}
					disabled={selectedRows.length < 1}
				>
					<Icon fontSize="medium">translate</Icon> {t("ASSETS.THEME.ACTIONS.translateAllQR")}
				</MenuItem>
				{/* Train category dialog */}
				<ConfirmDialogButton
					onConfirm={(e, values) => {
						const codes = selectedRows.map(row => row.code);

						const id = v4();

						// Display a message to the user
						dispatch(
							display({
								id,
								message: t("ASSETS.TRAIN.starting"),
								type: "loading"
							})
						);

						startCategoryTraining(codes, "category", id);

						setAnchorElMenu(null);
					}}
					component={
						<MenuItem disabled={selectedRows.length < 1}>
							<Icon fontSize="medium" sx={{ mr: 1 }}>
								model_training
							</Icon>
							{t("ASSETS.THEME.ACTIONS.trainAllQR")}
						</MenuItem>
					}
					title={t("ASSETS.THEME.startTrainingCategoryTitle")}
					content={t("ASSETS.THEME.startTrainingCategoryDescription")}
				/>

				{/* Untrain category dialog */}
				<ConfirmDialogButton
					onConfirm={(e, values) => {
						const codes = selectedRows.map(row => row.code);

						dispatch(
							display({
								message: t("ASSETS.THEME.questionsUntrainingStarted"),
								type: "info"
							})
						);

						startCategoryUntraining(
							codes,
							"category",
							() => {
								dispatch(
									display({
										message: t("ASSETS.THEME.questionsUntrainingFinish"),
										type: "success"
									})
								);
							},
							() => {
								dispatch(
									display({
										message: t("ASSETS.THEME.untrainingError"),
										type: "error"
									})
								);
							}
						);

						setAnchorElMenu(null);
					}}
					component={
						<MenuItem style={{ color: "red" }} disabled={selectedRows.length < 1}>
							<Icon fontSize="medium" sx={{ mr: 1 }}>
								model_training
							</Icon>
							{t("ASSETS.THEME.ACTIONS.untrainAllQR")}
						</MenuItem>
					}
					title={t("ASSETS.THEME.startUntrainingCategoryTitle")}
					content={t("ASSETS.THEME.startUntrainingCategoryDescription")}
				/>

				{/* Delete */}
				<ConfirmDialogButton
					disabled={selectedRows.length < 1}
					onConfirm={async (e, values) => {
						// onDeleteFolder(folder);

						const ids = selectedRows.map(row => row._id);
						await deleteItems(
							ids,
							"answerItem",
							{
								delete: true,
								actions: [
									"deleteChildAnswerType", // Answer type cave have childs with parent = answerItem.code, delete theme.
									"emptyCacheTheme" // Empty cache for theme
								]
							},
							() => {
								getCategories();
							}
						);

						setSelectedRows([]);
						setAnchorElMenu(null);
					}}
					component={
						<MenuItem
							disabled={selectedRows.length < 1}
							style={{
								color: "red"
							}}
						>
							<Icon fontSize="medium">delete</Icon>
							{t("SETTINGS.delete")}
						</MenuItem>
					}
					title={t("ASSETS.DELETE_CONFIRMATION.title")}
					content={t("ASSETS.THEME.confirmDeleteCategories")}
				/>
			</Menu>
		</MDBox>
	];

	// Force menu position update when translations are ready
	useEffect(() => {
		if (ready && menuRefTrigger.current && Boolean(menuRef)) {
			// Small delay to ensure DOM is updated
			const timer = setTimeout(() => {
				if (menuRefTrigger.current) {
					menuRefTrigger.current.updatePosition();
				}
			}, 0);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [ready, menuRef, t]);

	useEffect(() => {
		getCategories();
	}, [folders, theme]);

	if (lod_.isNil(theme)) {
		return null;
	}

	return (
		<MDBox
			sx={{
				height: "100%"
			}}
		>
			<Card
				sx={{
					height: "100%",
					borderRadius: "0px"
				}}
			>
				<CardContent sx={{ m: 0, p: 0, display: "flex", overflow: "auto" }}>
					<MDBox
						display="flex"
						flexDirection="column"
						sx={{
							flex: 1
						}}
					>
						<MDBox display="flex" justifyContent="space-between" alignItems="center">
							<MDBox>
								<MDTypography variant="h5" sx={{ ml: 3, mt: 3 }}>
									{theme.name}
								</MDTypography>
								<MDTypography variant="subtitle2" sx={{ ml: 3 }}>
									{t("ASSETS.THEME.categoriesList")}
								</MDTypography>
							</MDBox>
						</MDBox>

						<MDBox
							sx={{
								overflow: "auto"
							}}
						>
							<DefaultDataTable
								// Checkbox
								checkbox
								selectedRows={selectedRows}
								handleSelection={(newValues, original) => {
									if (lod_.isNil(original)) {
										// Orignal null = we trigger the column checkbox
										if (!lod_.isEmpty(selectedRows)) {
											setSelectedRows([]);
										} else {
											setSelectedRows(categories);
										}
									} else {
										setSelectedRows(newValues);
									}
								}}
								isSelectedRow={row => {
									return selectedRows.some(selectedRow => selectedRow?.code === row?.code);
								}}
								list={null}
								pagination={{
									defaultEntrie: 50,
									entriesPerPage: [25, 50, 76, 100]
								}}
								noMargin
								noHeader
								headerAction={HEADER_ACTIONS}
								filters={{}}
								canSearch
								table={{
									rows: categories,
									columns: [
										{ accessor: "_id", Header: "id" },
										{ accessor: "code", Header: "code" },
										{ accessor: "name", Header: t("SETTINGS.PAGES.name") },
										{ accessor: "documentType", Header: "documentType" },
										{ accessor: "type", Header: "type" },
										{
											accessor: "languages",
											Header: t("ASSETS.THEME.languages"),
											Cell: LanguagesCell,
											Help: LanguagesHelp
										}
									],
									count: categories.length
								}}
								display={["name", "languages"]}
								compact
								actions={[
									<IconButton
										handleclick={({ values }, e) => {
											setMenuValues(values);
											setMenuRef(e.currentTarget);
											e.stopPropagation();
											e.preventDefault();
										}}
									>
										<Icon fontSize="medium">more_vert</Icon>
									</IconButton>
								]}
							/>
						</MDBox>
						{/* Menu actions selection */}
						<Menu
							open={Boolean(menuRef)}
							anchorEl={menuRef}
							onClose={() => {
								setMenuRef(null);
							}}
							action={menuRefTrigger}
						>
							{/* Options */}
							<MenuItem disabled style={{ opacity: 1 }}>
								<MDTypography variant="caption">{t("SETTINGS.edition")}</MDTypography>
							</MenuItem>
							{/* Edit */}
							<MenuItem
								disabled={menuValues.type === "multiCollection"}
								onClick={() => {
									onEdit(menuValues);
									setMenuRef(null);
								}}
							>
								<Icon fontSize="medium">edit</Icon>&nbsp;{t("SETTINGS.edit")}
							</MenuItem>
							{/* Delete */}
							<ConfirmDialogButton
								onConfirm={(e, values) => {
									handleDeleteFolder(values);
									setMenuRef(null);
								}}
								component={
									<MenuItem style={{ color: "red" }}>
										<Icon fontSize="medium">delete</Icon>&nbsp;{t("SETTINGS.delete")}
									</MenuItem>
								}
								rowValues={menuValues}
								displayAttribute="name"
							/>

							<Divider />
							{/* Actions */}
							<MenuItem disabled style={{ opacity: 1 }}>
								<MDTypography variant="caption">{t("SETTINGS.actions")}</MDTypography>
							</MenuItem>

							{/* Translate name */}
							<MenuItem
								onClick={() => {
									translateCategoryName([menuValues.code]);
									setMenuRef(null);
								}}
							>
								<Icon fontSize="medium">translate</Icon>
								{t("ASSETS.THEME.ACTIONS.translateCategoryName")}
							</MenuItem>
							{/* Translate content */}
							<MenuItem
								onClick={() => {
									translateQuestions([menuValues.code]);
									setMenuRef(null);
								}}
							>
								<Icon fontSize="medium">translate</Icon>
								{t("ASSETS.THEME.ACTIONS.translateAllQR")}
							</MenuItem>
							{/* Train category dialog */}
							<ConfirmDialogButton
								onConfirm={(e, values) => {
									const id = v4();

									// Display a message to the user
									dispatch(
										display({
											id,
											message: t("ASSETS.TRAIN.starting"),
											type: "loading"
										})
									);

									startCategoryTraining([menuValues.code], "category", id);
								}}
								component={
									<MenuItem>
										<Icon fontSize="medium" sx={{ mr: 1 }}>
											model_training
										</Icon>
										{t("ASSETS.THEME.ACTIONS.trainQR")}
									</MenuItem>
								}
								title={t("ASSETS.THEME.startTrainingCategoryTitle")}
								content={t("ASSETS.THEME.startTrainingCategoryDescription")}
							/>

							{/* Untrain category dialog */}
							<ConfirmDialogButton
								onConfirm={(e, values) => {
									dispatch(
										display({
											message: t("ASSETS.THEME.questionsUntrainingStarted"),
											type: "info"
										})
									);

									startCategoryUntraining(
										[menuValues.code],
										"category",
										() => {
											dispatch(
												display({
													message: t("ASSETS.THEME.questionsUntrainingFinish"),
													type: "success"
												})
											);
										},
										() => {
											dispatch(
												display({
													message: t("ASSETS.THEME.untrainingError"),
													type: "error"
												})
											);
										}
									);
								}}
								component={
									<MenuItem style={{ color: "red" }}>
										<Icon fontSize="medium" sx={{ mr: 1 }}>
											model_training
										</Icon>
										{t("ASSETS.THEME.ACTIONS.untrainQR")}
									</MenuItem>
								}
								title={t("ASSETS.THEME.startUntrainingCategoryTitle")}
								content={t("ASSETS.THEME.startUntrainingCategoryDescription")}
							/>
						</Menu>
					</MDBox>
				</CardContent>
			</Card>
		</MDBox>
	);
};

export default ThemeCard;
